// ds-allow-file: canvas-confetti draws on a canvas and needs literal colors
import { launchConfettiBursts } from "@src/lib/utils/confetti";

// Opus brand palette. canvas-confetti needs literal colors; keep in sync with
// the --brand-* variables in src/pages/global.css.
const CONFETTI_COLORS = ["#feda65", "#adbdff", "#775172", "#52154e", "#ffffff"];

const SHARED = {
  colors: CONFETTI_COLORS,
  ticks: 400,
  gravity: 0.7,
  decay: 0.92,
};

type Burst = Parameters<typeof launchConfettiBursts>[0][number];

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

const toViewport = (px: number, axis: "x" | "y") =>
  px / (axis === "x" ? window.innerWidth : window.innerHeight);

type CancelCelebration = () => void;

const launchWaves = (waves: [number, Burst[]][]): CancelCelebration => {
  const timers = waves.map(([delay, bursts]) =>
    setTimeout(() => launchConfettiBursts(bursts), delay),
  );
  return () => timers.forEach(clearTimeout);
};

const noop: CancelCelebration = () => undefined;

// Four waves of cannons and showers framing the inline headline card.
export const celebrateAroundCard = (rect: DOMRect): CancelCelebration => {
  if (prefersReducedMotion()) return noop;
  const left = toViewport(rect.left, "x");
  const right = toViewport(rect.right, "x");
  const top = toViewport(rect.top, "y");
  const middle = toViewport(rect.top + rect.height / 2, "y");
  const at = (fraction: number) =>
    toViewport(rect.left + rect.width * fraction, "x");
  const cannons = (particleCount: number, startVelocity: number): Burst[] => [
    {
      ...SHARED,
      particleCount,
      startVelocity,
      spread: 70,
      angle: 60,
      origin: { x: left, y: middle },
    },
    {
      ...SHARED,
      particleCount,
      startVelocity,
      spread: 70,
      angle: 120,
      origin: { x: right, y: middle },
    },
  ];
  const shower = (x: number, particleCount: number, spread: number): Burst => ({
    ...SHARED,
    particleCount,
    spread,
    startVelocity: 38,
    angle: 90,
    origin: { x, y: top },
  });
  return launchWaves([
    [0, cannons(150, 55)],
    [400, [shower(at(0.5), 160, 160)]],
    [900, cannons(110, 45)],
    [1400, [shower(at(0.25), 80, 100), shower(at(0.75), 80, 100)]],
  ]);
};

// Confetti erupting upward out of the envelope's opening.
export const celebrateFromEnvelope = (rect: DOMRect): CancelCelebration => {
  if (prefersReducedMotion()) return noop;
  const x = toViewport(rect.left + rect.width / 2, "x");
  const y = toViewport(rect.top + rect.height * 0.25, "y");
  const fountain = (
    particleCount: number,
    spread: number,
    startVelocity: number,
    angle = 90,
  ): Burst => ({
    ...SHARED,
    particleCount,
    spread,
    startVelocity,
    angle,
    origin: { x, y },
  });
  return launchWaves([
    [0, [fountain(180, 70, 60)]],
    [250, [fountain(90, 50, 55, 70), fountain(90, 50, 55, 110)]],
    [600, [fountain(140, 120, 45)]],
    [1000, [fountain(80, 160, 35)]],
  ]);
};

// A burst in every direction from the middle of the box, then cannons and
// showers framing it: the early opt-in's features have all switched on.
export const explodeFromCenter = (rect: DOMRect): CancelCelebration => {
  if (prefersReducedMotion()) return noop;
  const origin = {
    x: toViewport(rect.left + rect.width / 2, "x"),
    y: toViewport(rect.top + rect.height / 2, "y"),
  };
  const blast = (particleCount: number, startVelocity: number): Burst => ({
    ...SHARED,
    particleCount,
    startVelocity,
    spread: 360,
    origin,
  });
  const cancelBlast = launchWaves([
    [0, [blast(260, 60)]],
    [180, [blast(160, 40)]],
  ]);
  let cancelFrame = noop;
  const frame = setTimeout(() => {
    cancelFrame = celebrateAroundCard(rect);
  }, 300);
  return () => {
    cancelBlast();
    clearTimeout(frame);
    cancelFrame();
  };
};
